// The table boxes on Home. Each one is just its columns, in a Panel.
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import MailOutlined from '@mui/icons-material/MailOutlined';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import SendOutlined from '@mui/icons-material/SendOutlined';
import { KIND_LABEL, type Approval } from '../approvals/sample';
import { useApprovals } from '../approvals/useApprovals';
import type { Agent, Customer } from '@serb/shared';
import type { ActivityEvent } from '../communications/sample';
import { useCommunications } from '../communications/useCommunications';
import { useAgents } from '../agents/useAgents';
import { useAgentStats } from '../agents/stats';
import { useCustomers } from '../customers/useCustomers';
import { useCustomerStats } from '../customers/stats';
import type { ReminderItem } from '../reminders/model';
import { useReminders } from '../reminders/useReminders';
import { isActive, STAGE_TONE, type Shipment } from '../shipments/sample';
import { useShipments } from '../shipments/useShipments';
import { shortDate } from '../../lib/dates';
import { QUOTE_TONE, type Quotation } from '../quotations/sample';
import { useQuotations } from '../quotations/useQuotations';
import { money } from '../../lib/money';
import { RouteText } from '../shipments/ShipmentCard';
import { KindIcon } from '../approvals/KindIcon';
import { DataTable } from '../../components/DataTable';
import { Panel } from '../../components/Panel';
import { SrbCode } from '../../components/SrbCode';
import { DotLabel, StatusChip, StatusDot } from '../../components/StatusChip';
import { toneColor, type Tone } from '../../components/tone';

const COMM_ICON = {
  email: <MailOutlined fontSize="small" color="action" />,
  sent: <SendOutlined fontSize="small" color="action" />,
};

export function ApprovalsTablePanel() {
  const pending = useApprovals().items.filter((a) => a.status === 'pending');
  return (
    <Panel title="Needs Your Approval" viewAll="/approvals">
      <DataTable<Approval>
        rows={pending.slice(0, 4)}
        rowKey={(r) => r.id}
        empty="Nothing waiting for you."
        columns={[
          { header: 'SRB', primary: true, render: (r) => <SrbCode code={r.srb} /> },
          {
            header: 'Type',
            render: (r) => (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <KindIcon kind={r.kind} size={28} />
                <span>{KIND_LABEL[r.kind]}</span>
              </Stack>
            ),
          },
          { header: 'Description', clamp: 2, render: (r) => r.homeDescription },
          { header: 'Value / Details', nowrap: true, render: (r) => r.homeValue },
          { header: 'Received', nowrap: true, render: (r) => r.age },
          {
            header: '',
            action: true,
            render: (r) => (
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <Button component={RouterLink} to={`/approvals/${r.id}`} variant="contained" size="small">
                  Review
                </Button>
                <IconButton size="small" aria-label="More">
                  <MoreHoriz fontSize="small" />
                </IconButton>
              </Stack>
            ),
          },
        ]}
      />
    </Panel>
  );
}

export function RemindersTablePanel() {
  const { items, today } = useReminders();
  const todays = items.filter((r) => !r.complete && r.date === today);
  return (
    <Panel title="Today's Reminders" viewAll="/reminders">
      <DataTable<ReminderItem>
        rows={todays.slice(0, 5)}
        rowKey={(r) => r.id}
        empty="No reminders for today."
        columns={[
          {
            header: 'Time',
            primary: true,
            nowrap: true,
            render: (r) => (
              <Box
                sx={{
                  color: toneColor('info'),
                  bgcolor: `color-mix(in srgb, ${toneColor('info')} 12%, transparent)`,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1.5,
                  fontWeight: 700,
                }}
              >
                {r.time || '–'}
              </Box>
            ),
          },
          {
            header: 'Reminder',
            render: (r) => (
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{r.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {r.srb}
                </Typography>
              </Box>
            ),
          },
        ]}
      />
    </Panel>
  );
}

const TONE_ORDER: Tone[] = ['error', 'warning', 'info', 'neutral', 'success'];
const byTone = (a: Shipment, b: Shipment) => TONE_ORDER.indexOf(a.next.tone) - TONE_ORDER.indexOf(b.next.tone);

export function ShipmentsTablePanel() {
  const active = useShipments().items.filter(isActive);
  return (
    <Panel title="Active Shipments" viewAll="/shipments">
      <DataTable<Shipment>
        rows={active.slice(0, 5)}
        rowKey={(r) => r.srb}
        empty="No active shipments."
        columns={[
          { header: 'SRB', primary: true, render: (r) => <SrbCode code={r.srb} /> },
          { header: 'Route', clamp: 2, render: (r) => <RouteText s={r} /> },
          { header: 'Stage', nowrap: true, render: (r) => <StatusChip label={r.stage} tone={STAGE_TONE[r.stage]} /> },
          { header: 'ETA', nowrap: true, render: (r) => r.eta },
          { header: '', action: true, render: (r) => <StatusDot tone={r.next.tone} /> },
        ]}
      />
    </Panel>
  );
}

export function CommunicationsTablePanel() {
  const events = useCommunications().events;
  return (
    <Panel title="Recent Communications" viewAll="/communications">
      <DataTable<ActivityEvent>
        rows={events.slice(0, 5)}
        rowKey={(e) => e.id}
        empty="No messages yet."
        columns={[
          { header: 'Date', nowrap: true, primary: true, render: (e) => e.date },
          { header: '', action: true, render: (e) => COMM_ICON[e.status === 'Awaiting Reply' ? 'sent' : 'email'] },
          { header: 'From', render: (e) => e.org },
          { header: 'Subject', clamp: 2, render: (e) => e.subject },
          { header: 'SRB', render: (e) => <SrbCode code={e.srb} /> },
        ]}
      />
    </Panel>
  );
}

/** The next step of each active shipment, the most urgent first */
export function MilestonesTablePanel() {
  const rows = useShipments()
    .items.filter((s) => isActive(s) && s.next.due)
    .sort(byTone);
  return (
    <Panel title="Upcoming Milestones" viewAll="/shipments">
      <DataTable<Shipment>
        rows={rows.slice(0, 5)}
        rowKey={(r) => r.srb}
        empty="No upcoming steps."
        columns={[
          { header: 'Date', nowrap: true, primary: true, render: (r) => r.next.due },
          { header: 'Shipment', render: (r) => <SrbCode code={r.srb} /> },
          { header: 'Next step', clamp: 2, render: (r) => r.next.text },
          { header: 'Status', nowrap: true, render: (r) => <DotLabel label={r.stage} tone={r.next.tone} /> },
        ]}
      />
    </Panel>
  );
}

export function QuotationsTablePanel() {
  const { items, shipmentOf } = useQuotations();
  return (
    <Panel title="Quotations" viewAll="/quotations">
      <DataTable<Quotation>
        rows={items.slice(0, 4)}
        rowKey={(q) => q.number + q.srb}
        empty="No quotations yet."
        columns={[
          { header: 'SRB', primary: true, render: (q) => <SrbCode code={q.srb} /> },
          { header: 'Customer', clamp: 2, render: (q) => shipmentOf(q).customer },
          { header: 'Route', clamp: 2, render: (q) => <RouteText s={shipmentOf(q)} /> },
          { header: 'Value', nowrap: true, render: (q) => money(q.price, q.currency) },
          {
            header: 'Status',
            nowrap: true,
            render: (q) => <StatusChip label={q.status} tone={QUOTE_TONE[q.status]} />,
          },
          { header: 'Updated', nowrap: true, render: (q) => q.updated },
        ]}
      />
    </Panel>
  );
}

export function CustomersTablePanel() {
  const statsOf = useCustomerStats();
  return (
    <Panel title="Customers" viewAll="/customers">
      <DataTable<Customer>
        rows={useCustomers().items.slice(0, 5)}
        rowKey={(c) => c.id}
        empty="No customers yet."
        columns={[
          { header: 'Name', primary: true, render: (c) => c.name },
          { header: 'Recent Activity', render: (c) => statsOf(c).lastActivity },
          { header: 'Active Shipments', render: (c) => statsOf(c).active },
        ]}
      />
    </Panel>
  );
}

export function AgentsTablePanel() {
  const statsOf = useAgentStats();
  return (
    <Panel title="Overseas Agents" viewAll="/agents">
      <DataTable<Agent>
        rows={useAgents().items.slice(0, 5)}
        rowKey={(a) => a.id}
        empty="No agents yet."
        columns={[
          { header: 'Name', primary: true, render: (a) => a.name },
          {
            header: 'Recent Response',
            render: (a) => (statsOf(a).lastActivity ? shortDate(statsOf(a).lastActivity) : '—'),
          },
          { header: 'Open Requests', render: (a) => statsOf(a).pending + statsOf(a).notQuoted },
        ]}
      />
    </Panel>
  );
}
